/** Helpers de formatação e derivações visuais (dinheiro, datas, aging, iniciais e cor de avatar). */

/** Valor compacto em BRL, espelhando o mock: R$ 120M, R$ 1,2bi, R$ 60k, R$ 500. */
export function fmtMoney(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value >= 1_000_000_000) return `R$ ${(value / 1_000_000_000).toFixed(1).replace('.', ',')}bi`;
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(0)}M`;
  if (value >= 1_000) return `R$ ${(value / 1_000).toFixed(0)}k`;
  return `R$ ${value}`;
}

/** Valor exato em BRL (CSV / tooltips). String vazia quando nulo. */
export function fmtMoneyExact(value: number | null | undefined): string {
  if (value == null) return '';
  return `R$ ${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

/** Dias decorridos desde uma data (UTC ISO) até agora; 0 se inválida. */
export function daysSince(isoUtc: string): number {
  const then = new Date(isoUtc).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

/** Data curta pt-BR (dd/mm/aa). */
export function fmtDateShort(isoUtc: string): string {
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}/${mm}/${yy}`;
}

/**
 * Data local (não UTC) em 'yyyy-MM-dd', para <input type="date"> e defaults de "hoje". Achado F7
 * (revisão 2026-07): `new Date().toISOString().slice(0,10)` usa UTC — das 21h às 23h59 em Brasília
 * (UTC-3) o "hoje" viraria amanhã. Centraliza o que antes era copiado localmente em 4 arquivos
 * (NewDealModal, CriticalTransitionModal, CommitteesSection, TasksSection) + o já existente em
 * FunnelDashboardView.
 */
export function isoLocalDate(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Converte número digitado em pt-BR e devolve número ou null (vazio/inválido → null). Se há vírgula,
 * ela é o separador decimal e pontos são milhar ("2.500,5" → 2500.5). Sem vírgula, o ponto é o
 * decimal ("2.1" → 2.1) — evita que "2.1" vire 21 ao remover o ponto cegamente.
 * Achado F15 (revisão 2026-07): antes copiado byte-a-byte em useHandoffDeal.ts e portfolio/types.ts;
 * fonte única aqui, os dois arquivos re-exportam com o nome antigo (sem tocar os importadores).
 */
export function parseDecimalPtBr(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const normalized = trimmed.includes(',')
    ? trimmed.replace(/\./g, '').replace(',', '.')
    : trimmed;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/** Iniciais a partir do nome: 1ª letra das duas pontas; ou 2 letras se for uma palavra só. */
export function initials(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return '–';
  const parts = n.split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

/**
 * Cor de avatar determinística a partir de uma chave (id ou nome). Paleta de tons abafados, no
 * espírito do mock (que fixa cores por pessoa; aqui derivamos de forma estável e consistente).
 */
const AVATAR_PALETTE = [
  '#3a4a6b',
  '#6b3a4a',
  '#4a6b3a',
  '#6b563a',
  '#3a6b6b',
  '#553a6b',
  '#6b3a3a',
  '#3a5a6b',
  '#5a6b3a',
];

export function avatarColor(key: string | null | undefined): string {
  const k = key ?? '';
  let hash = 0;
  for (let i = 0; i < k.length; i += 1) hash = (hash * 31 + k.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length] ?? '#3a4a6b';
}
